import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Download, 
  Database,
  RefreshCw,
  Loader2,
  FileJson,
  Clock,
  HardDrive,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLLECTION_LABELS = {
  students: "Students",
  teachers: "Teachers",
  staff: "Staff",
  classes: "Classes",
  sections: "Sections",
  subjects: "Subjects",
  fees: "Fees",
  fee_payments: "Fee Payments",
  fee_structures: "Fee Structures",
  attendance: "Student Attendance",
  staff_attendance: "Staff Attendance",
  salary_structures: "Salary Structures",
  salary_slips: "Salary Slips",
  exams: "Exams",
  exam_results: "Exam Results",
  marks: "Marks",
  homework: "Homework",
  notices: "Notices",
  timetable: "Timetable",
  hostel_rooms: "Hostel Rooms",
  hostel_allocations: "Hostel Allocations",
  transport_routes: "Transport Routes",
  transport_allocations: "Transport Allocations",
  library_books: "Library Books",
  book_issues: "Book Issues",
  documents: "Documents",
  users: "Users (without passwords)"
};

const DataBackupManager = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [backupHistory, setBackupHistory] = useState([]);
  const [selectAll, setSelectAll] = useState(true);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/schools/backup/preview`, {
        headers: getAuthHeader()
      });
      setPreview(res.data);
      // Select all by default
      setSelectedCollections(Object.keys(res.data.collections));
    } catch (error) {
      toast.error('Failed to fetch backup preview');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schools/backup/history`, {
        headers: getAuthHeader()
      });
      setBackupHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch backup history');
    }
  };

  const isAdmin = user && ['super_admin', 'school_admin'].includes(user.role);

  useEffect(() => {
    if (isAdmin) {
      fetchPreview();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Only admin can use this
  if (!isAdmin) {
    return null;
  }

  const toggleCollection = (collection) => {
    setSelectedCollections(prev => 
      prev.includes(collection) 
        ? prev.filter(c => c !== collection)
        : [...prev, collection]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(Object.keys(preview?.collections || {}));
    }
    setSelectAll(!selectAll);
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const collectionsParam = selectAll ? '' : `?collections=${selectedCollections.join(',')}`;
      const res = await axios.get(
        `${API_URL}/api/schools/backup/download${collectionsParam}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = res.headers['content-disposition'];
      let filename = `backup_${new Date().toISOString().split('T')[0]}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const getTotalSelected = () => {
    if (!preview) return 0;
    return selectedCollections.reduce((sum, col) => sum + (preview.collections[col] || 0), 0);
  };

  return (
    <Card data-testid="data-backup-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          Data Backup & Export
        </CardTitle>
        <CardDescription>
          Download a complete backup of your school data in JSON format
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Refresh Button */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={fetchPreview} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {preview && (
            <div className="text-right">
              <p className="font-medium">{preview.school?.name}</p>
              <p className="text-sm text-muted-foreground">
                Total: {preview.total_records?.toLocaleString()} records
              </p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}

        {/* Collection Selection */}
        {preview && !loading && (
          <>
            {/* Select All Toggle */}
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Checkbox
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                id="select-all"
              />
              <label htmlFor="select-all" className="font-medium cursor-pointer">
                {selectAll ? 'All Collections Selected' : 'Select All Collections'}
              </label>
              <Badge variant="secondary" className="ml-auto">
                {getTotalSelected().toLocaleString()} records
              </Badge>
            </div>

            {/* Collections Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto p-2">
              {Object.entries(preview.collections).map(([collection, count]) => (
                <div
                  key={collection}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    selectedCollections.includes(collection)
                      ? 'bg-green-50 dark:bg-green-950 border-green-300'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => toggleCollection(collection)}
                >
                  <Checkbox
                    checked={selectedCollections.includes(collection)}
                    onChange={() => toggleCollection(collection)}
                  />
                  <Database className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm flex-1">{COLLECTION_LABELS[collection] || collection}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>

            {/* Download Button */}
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={downloadBackup} 
                disabled={downloading || selectedCollections.length === 0}
                className="flex-1"
                data-testid="download-backup-btn"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download Backup ({getTotalSelected().toLocaleString()} records)
              </Button>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
              <p className="flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                Backup will be downloaded as a JSON file
              </p>
              <p className="text-muted-foreground mt-1">
                User passwords are excluded from the backup for security
              </p>
            </div>
          </>
        )}

        {/* Backup History */}
        {backupHistory.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4" />
              Recent Backups
            </h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {backupHistory.slice(0, 5).map((log, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>{new Date(log.backup_date).toLocaleString()}</span>
                  <Badge variant="secondary" className="ml-auto">
                    {log.total_records?.toLocaleString()} records
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataBackupManager;
